import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Pause, Play, Trash2 } from 'lucide-react';
import { useLogcatChunks, useLogcatStream } from '@/lib/logcat/useLogcatStream';
import { matchLine, parseFilter } from '@/lib/logcat/filter';
import { levelClass, type LogLine } from '@/lib/logcat/parser';

interface Props {
  serial: string;
}

const TAIL_SIZE = 800; // keep at most this many lines in the panel ring buffer

/**
 * Minimal logcat panel for the floating mirror. Reuses the shared
 * [`useLogcatStream`](../lib/logcat/useLogcatStream.ts) bundle so it doesn't
 * open an extra WebSocket — if the user is also viewing the full Logs page,
 * both panels share the same upstream connection and ring buffer.
 *
 * Filter expression follows the Android Studio-style DSL (`tag:Foo`,
 * `level:W`, `tag~:^Re`, `-tag:Verbose`, etc.). Pause stops appending new
 * lines without dropping the upstream connection — useful for inspecting a
 * burst without the screen scrolling away.
 */
export function MiniLogcat({ serial }: Props): JSX.Element {
  const stream = useLogcatStream(serial);
  const [expr, setExpr] = useState('');
  const [tail, setTail] = useState<LogLine[]>([]);
  const tailRef = useRef<LogLine[]>([]);

  const { terms, errors } = useMemo(() => parseFilter(expr), [expr]);

  // Reset the tail whenever the filter changes — we don't keep a scrollback
  // of unmatched lines in this panel; the full Logs page is the place for that.
  useEffect(() => {
    tailRef.current = [];
    setTail([]);
  }, [expr, serial]);

  useLogcatChunks(serial, (chunk, cleared) => {
    if (cleared) {
      tailRef.current = [];
      setTail([]);
      return;
    }
    if (stream.paused) return;
    let added = false;
    for (const ln of chunk) {
      if (terms.length === 0 || matchLine(ln, terms)) {
        tailRef.current.push(ln);
        added = true;
      }
    }
    if (!added) return;
    if (tailRef.current.length > TAIL_SIZE) {
      tailRef.current.splice(0, tailRef.current.length - TAIL_SIZE);
    }
    setTail(tailRef.current.slice());
  });

  /* auto-scroll to bottom on new lines */
  const scrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [tail]);

  return (
    <div className="flex h-full flex-col border-t border-line bg-bg-card">
      <div className="flex h-8 items-center gap-1 border-b border-line px-2 text-[11px]">
        <input
          className="h-6 flex-1 rounded border border-line bg-bg px-2 font-mono text-[11px] outline-none focus:border-accent"
          placeholder="过滤：tag:Foo  level:W  -tag:Verbose"
          value={expr}
          onChange={(e) => setExpr(e.target.value)}
        />
        <button
          className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-bg"
          title={stream.paused ? '继续' : '暂停'}
          onClick={() => stream.setPaused(!stream.paused)}
        >
          {stream.paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
        </button>
        <button
          className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-bg"
          title="清空"
          onClick={() => {
            stream.clear();
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
        <span
          className={clsx(
            'ml-1 text-[10px]',
            stream.status === 'open'
              ? 'text-emerald-400'
              : stream.status === 'connecting'
              ? 'text-amber-400'
              : 'text-rose-400'
          )}
          title={stream.statusMessage ?? stream.status}
        >
          ●
        </span>
      </div>

      {errors.length > 0 && (
        <div className="border-b border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[10px] text-rose-300">
          {errors.join('；')}
        </div>
      )}

      <div
        ref={scrollerRef}
        className="flex-1 overflow-auto px-2 py-1 font-mono text-[11px] leading-snug"
      >
        {tail.length === 0 ? (
          <div className="px-1 py-2 text-ink-dim">
            {stream.status === 'open' ? '等待匹配的日志…' : '正在连接 logcat…'}
          </div>
        ) : (
          tail.map((l, i) => <Row key={i} line={l} />)
        )}
      </div>

      <div className="flex h-5 items-center justify-between border-t border-line bg-bg-card/95 px-2 text-[10px] text-ink-dim">
        <span>
          显示 {tail.length} / 共 {stream.totalSeen}
        </span>
        {stream.paused && <span className="text-amber-400">已暂停</span>}
      </div>
    </div>
  );
}

function Row({ line }: { line: LogLine }): JSX.Element {
  const lvCls = levelClass(line.level);
  if (!line.ts) {
    return (
      <div className="whitespace-pre-wrap break-all px-1 text-ink-dim">
        {line.raw}
      </div>
    );
  }
  return (
    <div className="whitespace-pre-wrap break-all px-1">
      <span className="text-ink-dim">{line.ts}</span>{' '}
      <span className={clsx('font-bold', lvCls)}>{line.level}</span>{' '}
      <span className="text-ink">{line.tag}</span>
      <span className="text-ink-dim">: </span>
      <span className={lvCls}>{line.message}</span>
    </div>
  );
}
